import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelectedCompany } from "../Commoncontext/SelectedCompanyContext";
import { useUsers } from "../Commoncontext/UsersContext";
import { useAnnouncements } from "../Commoncontext/AnnouncementsContext";
import IconAnnouncements from "../Images/IconAnnouncements";
import EyeIcon from "../Images/EyeIcon";
import Modal from "../Components/Modal";
import SimpleTable from "../Components/SimpleTable";
import MoreCategoriesButton from "../Components/MoreCategoriesButton";
import CompanyUtils from "../Utils/CompanyUtils";
import { getAttachmentCount } from "../Utils/AnnouncementsUtil";
import {
  canCreateAnnouncement,
  canDeleteAnnouncement,
} from "../Utils/annoucementsPermissions";
import useResponsive from "../hooks/useResponsive";
import { VIEW_BREAKPOINTS } from "../Config/responsive";
import styles from "../Styles/Announcementlist.module.css";

function Announcements() {
  const navigate = useNavigate();
  const { selectedCompanyId } = useSelectedCompany();
  const { user } = useUsers();
  const { width } = useResponsive();
  const isCompactAnnouncementsView =
    width <= VIEW_BREAKPOINTS.announcementsTableMin;

  const {
    announcements = [],
    fetchAnnouncements,
    loading,
    markViewed,
    deleteAnnouncement,
    setActive,
    fetchAnnouncementById,
  } = useAnnouncements();

  useEffect(() => {
    if (selectedCompanyId && typeof fetchAnnouncements === "function") {
      fetchAnnouncements(selectedCompanyId);
    }
  }, [selectedCompanyId, fetchAnnouncements]);

  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [q, setQ] = useState("");
  const [mobileVisibleCount, setMobileVisibleCount] = useState(7);

  const canCreate = useMemo(
    () => canCreateAnnouncement(user, selectedCompanyId),
    [user, selectedCompanyId]
  );

  const canDelete = useMemo(
    () => canDeleteAnnouncement(user, selectedCompanyId),
    [user, selectedCompanyId]
  );

  const visible = useMemo(
    () =>
      (announcements || []).filter((a) =>
        CompanyUtils.isUserInTargetGroup(a, user)
      ),
    [announcements, user]
  );

  const categoryItems = useMemo(() => {
    const set = new Set();
    visible.forEach((a) => a.category && set.add(a.category));
    const sorted = Array.from(set).sort((a, b) =>
      String(a).localeCompare(String(b), "fi", { sensitivity: "base" })
    );
    const visibleCategories = sorted.slice(0, 4);
    return [
      { value: "all", label: "Kaikki" },
      ...visibleCategories.map((k) => ({ value: k, label: k })),
    ];
  }, [visible]);

  const extraCategories = useMemo(() => {
    const set = new Set();
    visible.forEach((a) => a.category && set.add(a.category));
    const sorted = Array.from(set).sort((a, b) =>
      String(a).localeCompare(String(b), "fi", { sensitivity: "base" })
    );
    return sorted.slice(4);
  }, [visible]);

  const filteredAnnouncements = useMemo(() => {
    const searchLower = q.trim().toLowerCase();

    return visible.filter((a) => {
      const categoryOk =
        categoryFilter === "all" ? true : a.category === categoryFilter;

      const searchOk = !searchLower
        ? true
        : `${a.title ?? ""} ${a.content ?? ""} ${a.category ?? ""}`
            .toLowerCase()
            .includes(searchLower);

      return categoryOk && searchOk;
    });
  }, [visible, categoryFilter, q]);

  useEffect(() => {
    setMobileVisibleCount(7);
  }, [categoryFilter, q]);

  const mobileAnnouncements = useMemo(() => {
    return filteredAnnouncements.slice(0, mobileVisibleCount);
  }, [filteredAnnouncements, mobileVisibleCount]);

  const openAnnouncement = useCallback(
    (a) => {
      if (!a?.id) return;

      setActive?.(a);
      navigate(`/announcementnew2/${a.id}`);

      try {
        const uid = user?.uid;
        if (uid && (!a.viewedBy || !a.viewedBy.includes(uid))) {
          Promise.resolve()
            .then(() => markViewed?.(a.id, uid, selectedCompanyId))
            .catch(() => {});
        }
      } catch {}

      try {
        fetchAnnouncementById?.(a.id, { preferCache: true, force: false });
      } catch {}
    },
    [
      navigate,
      setActive,
      user?.uid,
      markViewed,
      selectedCompanyId,
      fetchAnnouncementById,
    ]
  );

  const askDelete = (a) => {
    setSelectedAnnouncement(a);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    await deleteAnnouncement(selectedCompanyId, selectedAnnouncement.id);
    setShowDeleteModal(false);
    setSelectedAnnouncement(null);
  };

  const handleCreateNew = () => navigate("/announcementnew2");

  const columns = useMemo(
    () => [
      {
        key: "_new",
        header: "",
        width: 110,
        render: (a) => {
          const label = CompanyUtils.getTimeButtonLabel(a?.createdAt);
          return label ? (
            <span className={styles.timePillSm} title={label}>
              {label.replace(" ", "\u00a0")}
            </span>
          ) : (
            ""
          );
        },
        sortAccessor: (a) =>
          Math.floor(CompanyUtils.toMillis(a?.createdAt) / 1000),
      },
      {
        key: "title",
        header: "Otsikko",
        grow: 1,
        render: (a) => {
          const full = a.title || "";
          const short = full.length > 65 ? `${full.slice(0, 65)}…` : full;
          return (
            <span
              title={full}
              className={styles.titleEllipsis}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {short}
              {a.hasAttachments && (
                <span
                  title="Tiedotteessa on liitteitä"
                  aria-label="Liitteitä"
                  style={{ fontSize: "0.9em", opacity: 0.7 }}
                >
                  📎
                </span>
              )}
            </span>
          );
        },
        sortAccessor: (a) => (a.title ?? "").toLowerCase(),
      },
      {
        key: "createdAt",
        header: "Lähetetty",
        width: 180,
        accessor: (a) => {
          const ms = CompanyUtils.toMillis(a?.createdAt);
          if (!ms) return "—";
          const d = new Date(ms);
          return (
            d.toLocaleDateString("fi-FI", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            }) +
            " : " +
            d.toLocaleTimeString("fi-FI", {
              hour: "2-digit",
              minute: "2-digit",
            })
          );
        },
        sortAccessor: (a) =>
          Math.floor(CompanyUtils.toMillis(a?.createdAt) / 1000),
      },
      {
        key: "viewedBy",
        header: "Katsottu",
        width: 120,
        render: (a) => {
          const count = Number(a.viewedByCount || 0);
          return (
            <span
              className={styles.eyeWrap}
              title={`Katsonut ${count} käyttäjää`}
            >
              <EyeIcon
                className={count < 1 ? styles.eyeIconMuted : styles.eyeIcon}
              />
              <span className={styles.eyeCount}>{count}</span>
            </span>
          );
        },
        sortAccessor: (a) => Number(a.viewedByCount || 0),
      },
      {
        key: "category",
        header: "Kategoria",
        width: 200,
        render: (a) => (
          <span className={styles.categoryPillSm} title={a.category}>
            {a.category || "-"}
          </span>
        ),
        sortAccessor: (a) => (a.category ?? "").toLowerCase(),
      },
    ],
    []
  );

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        {!isCompactAnnouncementsView && (
          <div className={styles.pageTitle}>
            <div className={styles.titleIcon}>
              <IconAnnouncements />
            </div>
            <h1>Tiedotteet</h1>

            {canCreate && (
              <div className={styles.mlAuto}>
                <button className={styles.primaryBtn} onClick={handleCreateNew}>
                  + Uusi tiedote
                </button>
              </div>
            )}
          </div>
        )}

        {!isCompactAnnouncementsView && (
          <div className={styles.tableWrap}>
            <SimpleTable
              columns={columns}
              data={visible}
              initialSort={{ key: "createdAt", dir: "desc" }}
              onRowClick={openAnnouncement}
              actionsHeader=""
              actionsRender={(a) => (
                <div
                  className={styles.rowMenu}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button className={styles.menuBtn}>⋮</button>
                  <div className={styles.menuDropdown}>
                    <div
                      className={styles.menuItem}
                      onClick={() => openAnnouncement(a)}
                    >
                      Avaa
                    </div>
                    {canDelete && (
                      <div
                        className={styles.menuItemDanger}
                        onClick={() => askDelete(a)}
                      >
                        Poista
                      </div>
                    )}
                  </div>
                </div>
              )}
              filters={{
                pills: [
                  {
                    field: "category",
                    value: categoryFilter,
                    onChange: setCategoryFilter,
                    items: categoryItems,
                  },
                ],
                renderAfterPills: () =>
                  extraCategories.length > 0 ? (
                    <MoreCategoriesButton
                      categories={extraCategories}
                      onSelect={(cat) => setCategoryFilter(cat)}
                    />
                  ) : null,
              }}
              search={{
                value: q,
                onChange: setQ,
                placeholder: "Haku: otsikko, sisältö, kategoria…",
                keys: (a) =>
                  `${a.title ?? ""} ${a.content ?? ""} ${a.category ?? ""}`,
              }}
              pageSize={7}
              tableClassName={styles.userTable}
              headClassName={styles.tableHead}
              loading={!!loading}
            />
          </div>
        )}

        {isCompactAnnouncementsView && (
          <div className={styles.mobileWrap}>
            <div className={styles.mobileSearchWrap}>
              <div className={styles.mobileSearchField}>
                <span className={styles.mobileSearchIcon} aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                    <circle
                      cx="11"
                      cy="11"
                      r="6.5"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <path
                      d="M16 16L21 21"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <input
                  className={styles.mobileSearchInput}
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Hae tiedotteita…"
                />
              </div>
            </div>

            <div className={styles.mobileCategoryBar}>
              {categoryItems.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  className={`${styles.mobileCategoryFilterBtn} ${
                    categoryFilter === item.value
                      ? styles.mobileCategoryFilterBtnActive
                      : ""
                  }`}
                  onClick={() => setCategoryFilter(item.value)}
                >
                  {item.label}
                </button>
              ))}

              {extraCategories.length > 0 && (
                <MoreCategoriesButton
                  categories={extraCategories}
                  onSelect={(cat) => setCategoryFilter(cat)}
                />
              )}
            </div>

            <div className={styles.mobileList}>
              {mobileAnnouncements.map((a) => {
                const ms = CompanyUtils.toMillis(a?.createdAt);
                const createdLabel = ms
                  ? new Date(ms).toLocaleDateString("fi-FI", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    }) +
                    " " +
                    new Date(ms).toLocaleTimeString("fi-FI", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })
                  : "—";

                const count = Number(a.viewedByCount || 0);
                const newLabel = CompanyUtils.getTimeButtonLabel(a?.createdAt);

                return (
                  <button
                    key={a.id}
                    type="button"
                    className={styles.mobileCard}
                    onClick={() => openAnnouncement(a)}
                  >
                   <div className={styles.mobileCardContent}>
                     <div className={styles.mobileCardTopRow}>
                       <span className={styles.mobileCardCategoryText}>
                        {a.category || "-"}
                       </span>
                      {newLabel && (
                        <span className={styles.mobileNewBadge}>
                          {newLabel}
                        </span>
                       )}
                    </div>
                    <div className={styles.mobileCardTitleRow}>
                      <div className={styles.mobileCardTitle}>
                        {a.title || "—"}
                      </div>
                    </div>
                    <div className={styles.mobileCardMetaRow}>
                      <span className={styles.mobileCardDate}>
                        {createdLabel}
                      </span>
                      <span className={styles.mobileMetaSeparator}>•</span>
                      <span className={styles.mobileCardViews}>
                        <EyeIcon
                          className={
                            count < 1 ? styles.eyeIconMuted : styles.eyeIcon
                          }
                        />
                        <span>{count}</span>
                      </span>
                     {getAttachmentCount(a) > 0 && (
                       <span className={styles.mobileCardAttachments}>
                         <span className={styles.mobileAttachmentIcon}>📎</span>
                         <span>{getAttachmentCount(a)}</span>
                       </span>
                     )}
                </div>
                
              </div>
               <span className={styles.mobileChevron}>›</span>
            </button>
            );
              })}

              {!loading && filteredAnnouncements.length > mobileVisibleCount && (
                <div className={styles.mobileLoadMoreWrap}>
                  <div className={styles.mobileResultCount}>
                    Näytetään{" "}
                    {Math.min(mobileVisibleCount, filteredAnnouncements.length)} /{" "}
                    {filteredAnnouncements.length}
                  </div>

                  <button
                    type="button"
                    className={styles.mobileLoadMoreBtn}
                    onClick={() => setMobileVisibleCount((prev) => prev + 7)}
                  >
                    Näytä lisää
                  </button>
                </div>
              )}

              {!loading && filteredAnnouncements.length === 0 && (
                <div className={styles.mobileEmptyState}>
                  Ei hakuehtoihin sopivia tiedotteita.
                </div>
              )}
            </div>
          </div>
        )}

        {showDeleteModal && selectedAnnouncement && (
          <Modal
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedAnnouncement(null);
            }}
            onConfirm={confirmDelete}
            showCancelButton
            size="small"
          >
            <p className="centerAligned">Haluatko varmasti poistaa tiedotteen?</p>
          </Modal>
        )}
      </div>
    </div>
  );
}

export default Announcements;